import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'

import { get } from 'react-hook-form'
import { Box, Grid, Typography } from '@material-ui/core'
import { useStoreActions, useStoreState } from 'easy-peasy'

import Faqs from 'artkive/components/Faqs'
import ImageContainer from 'artkive/components/ImageContainer'
import MarkdownText from 'artkive/components/MarkdownText'
import HowItWorks from 'artkive/components/PageSections/HowItWorks'
import OtherProductsSection from 'artkive/components/sections/OtherProductsSection'
import { PRODUCT_SLIDES } from 'artkive/components/sections/ProductsCarousel/ProductsCarousel.constants'
import Swipe from 'artkive/components/Swipe'
import useMobileBreakpoint from 'artkive/hooks/useMobileBreakpoint'
import { useVariableState } from 'artkive/hooks/useVariableStore'
import { BOX_PRODUCT_TYPE } from 'artkive/stores/product.constants'
import routes from 'artkive/utils/routes'
import theme from 'artkive/utils/theme'
import { trackConversion } from 'artkive/utils/tracker'

import topLines from 'images/ecom/get-started/lines.png'
import bottomLines from 'images/ecom/get-started/lines2.svg'

import ConfirmationCostsBox from './ConfirmationCostsBox'
import ConfirmationSocialBox from './ConfirmationSocialBox'
import { BoxGradient, useStyles } from './OrderConfirmation.style'
import VipMembershipConfirmation from './VipMembershipConfirmation'

const OrderConfirmation = ({ productData, mosaicPricing }) => {
  const { information, promoCode } = useVariableState(BOX_PRODUCT_TYPE, productData.uuid)
  const tracking = useStoreState(({ promoStore }) => promoStore.tracking)
  const { clearTracking } = useStoreActions(({ promoStore }) => promoStore)

  const isMobile = useMobileBreakpoint()
  const classes = useStyles()

  useEffect(() => {
    const data = new URLSearchParams(location.search).get('data')
    if (!data) return

    const { id, total } = JSON.parse(atob(data))
    trackConversion({
      orderId: id,
      total,
      email: information.email,
      currency: productData.currency_code,
      tracking,
      promoCode,
      productId: productData.id,
    })
    clearTracking()
  }, [])

  const data = useMemo(
    () => ({
      cover_url: get(productData, 'cover_image.url', ''),
      preview_url: get(productData, 'preview_image.url', ''),
      header: get(productData, 'properties.confirmation.header', ''),
      subheader: get(productData, 'properties.confirmation.subheader', ''),
      faqs: get(productData, 'properties.confirmation.faq.items', null),
      next_steps: get(productData, 'properties.confirmation.next_steps', null),
      vip_membership: get(productData, 'properties.vip_membership', false),
      cost_breakdown: get(productData, 'properties.confirmation.cost_breakdown', false),
      other_products: get(productData, 'properties.confirmation.other_products', false),
      ugc: get(productData, 'properties.confirmation.ugc', false),
    }),
    [productData],
  )

  const isWithMembership = false

  return (
    <Box className={classes.root}>
      <BoxGradient p={isMobile ? 0 : 8} mb={isMobile ? 0 : 12} flex={1}>
        <Grid container spacing={isMobile ? 0 : 8} className={classes.productContainer}>
          <Grid item xs={12} md={6} className={classes.container}>
            <ImageContainer src={data.preview_url || data.cover_url} className={classes.productImage} ratio={5 / 4}>
              <img className={classes.topLines} src={topLines} />
              <img className={classes.bottomLines} src={bottomLines} />
            </ImageContainer>
          </Grid>

          <Grid item xs={12} md={6} className={classes.description}>
            <Box className={classes.header} px={1}>
              <Typography gutterBottom variant='h2' className={classes.title}>
                {data.header}
              </Typography>
              <Typography gutterBottom variant='subtitle2' className={classes.subtitle}>
                <MarkdownText source={data.subheader} />
              </Typography>
            </Box>

            {data.vip_membership && !!isWithMembership && <VipMembershipConfirmation />}

            {data.cost_breakdown && <ConfirmationCostsBox mosaicPricing={mosaicPricing} />}
          </Grid>
        </Grid>
      </BoxGradient>

      {!!data.next_steps && (
        <HowItWorks title={data.next_steps.title} steps={data.next_steps.items} variant={data.next_steps.variant}>
          {data.next_steps.show_help_link && (
            <Box mt={4} textAlign='center'>
              <Swipe color='primary' href={routes.faqs} target='_blank' underline='none' variant='h6'>
                View FAQs
              </Swipe>
            </Box>
          )}
        </HowItWorks>
      )}

      {data.ugc && <ConfirmationSocialBox />}

      {data.other_products &&
        PRODUCT_SLIDES.map((slide, index) => (
          <OtherProductsSection key={slide.href} {...slide} mirrored={index % 2 === 1} colorIndex={index} />
        ))}

      {!!data.faqs && <Faqs bgcolor={theme.colors.white.dark} faqs={data.faqs} />}
    </Box>
  )
}

OrderConfirmation.propTypes = {
  mosaicPricing: PropTypes.object.isRequired,
  productData: PropTypes.object,
}

export default OrderConfirmation
